import { useCallback, useEffect, useRef, useState } from 'react'
import { useWidget } from '../../../../../providers/widget'
import { useExplorer } from '../../../providers'
import { ExploreWidgetConfig, SearchbarChip, VisitorFilter } from '../../../config'
import { isRedirectMode, useSearchSubmit } from '../searchShared'
import { InlineIconSvg } from '../../../../../common/inline-icon-svg'

/**
 * Quick-filter chip row (#853, toolkit: SBP filter_chips). SINGLE-select:
 * activating a chip applies its resolved Central ID under its visitor-filter
 * key immediately and clears every other chip-owned value; clicking the active
 * chip clears it. Desktop drag-to-scroll ports the SBP idiom: 5px dead zone,
 * pointer capture, inertial flick (exponential decay), the click after a drag
 * release swallowed in capture phase, and edge fade masks only when content is
 * actually cut off.
 *
 * #1255 (Eli decision 2026-08-28, module parity): in hero/redirect mode the
 * toolkit's applyNow() runs submitNow() and navigates on chip tap
 * (searchbar-pro.js:4331) instead of live-refining. Gated through the same
 * isRedirectMode() every other redirect-gated behavior uses. The patch is
 * handed to submitSearch as an override rather than applied via setFilter
 * first: setFilter dispatches to a reducer, so state.visitorFilters would
 * not reflect it yet if submit ran right after in the same handler — the
 * override lets the redirect URL be built from the correct value without a
 * second URL builder.
 *
 * #871 (PR #1415 direction change, Eli 2026-09-07): the chip icon is the
 * stored `iconSvg` markup rendered INLINE through the shared InlineIconSvg
 * helper, which re-runs the icon sanitizer on every render. The legacy
 * `iconUrl` (remote image URL) is deliberately never read here: a customer
 * page must not fetch a third-party host (api.iconify.design or anything
 * else) on every load, the same call #1423 made for amenity icons. A chip
 * with only an iconUrl renders icon-less until the operator re-selects it.
 */

const DRAG_DEAD_ZONE_PX = 5
const FLICK_MIN_VELOCITY = 0.3 // px/ms
const FLICK_DECAY_PER_MS = 0.99
const FLICK_STOP_VELOCITY = 0.02
const HOLD_STILL_MS = 80

const chipActive = (chip: SearchbarChip, filters: Partial<VisitorFilter>) =>
  (filters[chip.filterKey] as string | undefined) === String(chip.centralId)

export default function QuickChips() {
  const { options } = useWidget<ExploreWidgetConfig>()
  const { state, setFilter } = useExplorer()
  const submitSearch = useSearchSubmit()
  const redirectMode = isRedirectMode(options)
  const chips = options.searchbar.chips
  const trackRef = useRef<HTMLDivElement>(null)
  const [fades, setFades] = useState({ left: false, right: false })

  const drag = useRef({
    pointerId: -1,
    startX: 0,
    startScroll: 0,
    dragging: false,
    dragged: false,
    lastX: 0,
    lastT: 0,
    velocity: 0,
    raf: 0,
  })

  const updateFades = useCallback(() => {
    const el = trackRef.current
    if (!el) return
    setFades({
      left: el.scrollLeft > 1,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
    })
  }, [])

  useEffect(() => {
    updateFades()
    const el = trackRef.current
    if (!el) return
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateFades) : null
    ro?.observe(el)
    return () => ro?.disconnect()
  }, [updateFades, chips.items.length])

  const stopFlick = () => {
    if (drag.current.raf) cancelAnimationFrame(drag.current.raf)
    drag.current.raf = 0
  }

  const onPointerDown = (e: React.PointerEvent) => {
    // Mouse/pen only — touch pans natively via overflow-x (toolkit parity).
    if (e.pointerType === 'touch') return
    stopFlick()
    const d = drag.current
    // Reset the click-swallow flag: a drag that ended via pointercancel never
    // produced the click that clears it, and must not eat the NEXT tap.
    d.dragged = false
    d.pointerId = e.pointerId
    d.startX = e.clientX
    d.startScroll = trackRef.current?.scrollLeft ?? 0
    d.dragging = false
    d.lastX = e.clientX
    d.lastT = e.timeStamp
    d.velocity = 0
  }

  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current
    if (d.pointerId !== e.pointerId) return
    const el = trackRef.current
    if (!el) return
    const dx = e.clientX - d.startX
    if (!d.dragging) {
      if (Math.abs(dx) < DRAG_DEAD_ZONE_PX) return
      d.dragging = true
      d.dragged = true
      el.setPointerCapture(e.pointerId)
    }
    el.scrollLeft = d.startScroll - dx
    const dt = e.timeStamp - d.lastT
    if (dt > 0) {
      d.velocity = (e.clientX - d.lastX) / dt
      d.lastX = e.clientX
      d.lastT = e.timeStamp
    }
    updateFades()
  }

  const onPointerUp = (e: React.PointerEvent) => {
    const d = drag.current
    if (d.pointerId !== e.pointerId) return
    d.pointerId = -1
    if (!d.dragging) return
    d.dragging = false
    const heldStill = e.timeStamp - d.lastT > HOLD_STILL_MS
    let v = -d.velocity
    if (!heldStill && Math.abs(v) > FLICK_MIN_VELOCITY) {
      const el = trackRef.current
      let lastTime = performance.now()
      const step = (now: number) => {
        if (!el) return
        const dt = now - lastTime
        lastTime = now
        el.scrollLeft += v * dt
        v *= Math.pow(FLICK_DECAY_PER_MS, dt)
        updateFades()
        if (Math.abs(v) > FLICK_STOP_VELOCITY) drag.current.raf = requestAnimationFrame(step)
      }
      drag.current.raf = requestAnimationFrame(step)
    }
  }

  // pointercancel never produces the click that clears `dragged`, and the
  // pointer-down reset can't cover it either (touch returns early above,
  // keyboard clicks have no pointer-down) — clear ALL drag state here so a
  // cancelled mouse drag can't swallow a later touch/keyboard activation.
  const onPointerCancel = (e: React.PointerEvent) => {
    const d = drag.current
    if (d.pointerId !== e.pointerId) return
    d.pointerId = -1
    d.dragging = false
    d.dragged = false
  }

  // Swallow the click that follows a drag release (capture phase) so a scroll
  // gesture never toggles a chip.
  const onClickCapture = (e: React.MouseEvent) => {
    if (drag.current.dragged) {
      e.preventDefault()
      e.stopPropagation()
      drag.current.dragged = false
    }
  }

  useEffect(() => () => stopFlick(), [])

  if (!chips.enabled || chips.items.length === 0) return null

  const toggleChip = (chip: SearchbarChip) => {
    const active = chipActive(chip, state.visitorFilters)
    const patch: Partial<VisitorFilter> = {}
    // Clear every chip-owned ACTIVE value first (single-select), then apply.
    for (const other of chips.items) {
      if (chipActive(other, state.visitorFilters)) patch[other.filterKey] = undefined as never
    }
    if (!active) patch[chip.filterKey] = String(chip.centralId) as never
    // Amenity chips take over the amenity dimension entirely: AdditionalFilters
    // submits under amenities_and when requireAllAmenities is on, and leaving
    // that key behind would AND an invisible constraint onto the chip's value.
    if (chip.filterKey === 'amenities') patch.amenities_and = undefined as never
    if (redirectMode) {
      // Hero mode has no on-page results to refine — apply-and-navigate,
      // module parity (#1255).
      submitSearch(patch)
    } else {
      setFilter(patch)
    }
  }

  return (
    <div className={`hfse-sbp-chips ${chips.showOnMobile ? 'hfse-sbp-chips-mobile' : ''} ${fades.left ? 'hfse-sbp-chips-fade-l' : ''} ${fades.right ? 'hfse-sbp-chips-fade-r' : ''}`}>
      <div
        className="hfse-sbp-chips-track"
        ref={trackRef}
        onScroll={updateFades}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onClickCapture={onClickCapture}
      >
        {chips.items.map((chip, i) => {
          const active = chipActive(chip, state.visitorFilters)
          return (
            <button
              key={`${chip.filterKey}-${chip.centralId}-${i}`}
              type="button"
              className={`hfse-sbp-chip ${active ? 'hfse-sbp-chip-active' : ''}`}
              aria-pressed={active}
              onClick={() => toggleChip(chip)}
            >
              {chip.iconSvg && <InlineIconSvg svg={chip.iconSvg} className="hfse-sbp-chip-icon" idPrefix={`hfse-chip${i}-`} />}
              {chip.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
